// src/home/components/MarketIntelligenceWidget.jsx
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Stack, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CollapsibleSection from '../../components/common/CollapsibleSection';
import KpiRow, { KpiCell } from '../../components/common/KpiRow';
import Pill from '../../components/common/Pill';
import StatTile from '../../components/common/StatTile';
import Panel from '../../components/common/Panel';
import { HOVER_LIFT_SX } from '../../theme/tokens';

const API = process.env.REACT_APP_API_BASE_URL;

const SIGNAL_ICONS = {
  competitor_aggression: WarningAmberIcon,
  category_decline:      TrendingDownIcon,
  price_dumping:         WarningAmberIcon,
  position_drift:        TrendingDownIcon,
  seasonal_anomaly:      InfoOutlinedIcon,
  discipline_proof:      InfoOutlinedIcon,
};

function SignalCard({ signal }) {
  const navigate = useNavigate();
  const Icon = SIGNAL_ICONS[signal.signal_type] || InfoOutlinedIcon;
  const isImmediate = signal.severity === 'immediate';

  return (
    <Panel
      pad="sm"
      onClick={() => navigate('/intelligence/market-lab')}
      sx={{
        ...HOVER_LIFT_SX,
        cursor: 'pointer',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Icon sx={{ fontSize: 18, color: isImmediate ? 'error.main' : 'warning.main', mt: 0.25, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
              {signal.headline}
            </Typography>
            {/* 🔴 W-33/D10: `Pill` statt MUI-`Chip`, und der linke Akzentbalken
                ist weg. `tokens.js` sagt in der V2-Notiz die Kanonregel woertlich:
                "never a left accent bar on a rounded surface" — ein 3px-Strich
                links an einer gerundeten Karte ist genau das. Die Dringlichkeit
                gehoert in die Pille, nicht an den Rand. */}
            <Pill tone={isImmediate ? 'err' : 'warn'} size="sm">
              {signal.severity}
            </Pill>
          </Stack>
          {signal.narrative && (
            <Typography variant="caption" color="text.secondary"
              sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {signal.narrative}
            </Typography>
          )}
        </Box>
      </Stack>
    </Panel>
  );
}

export default function MarketIntelligenceWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🔴 W-33/D10: ein eigener Fehlerzustand. Vorher schrieb `.catch(() =>
  // setData(null))` den Ausfall in denselben Wert wie "es gibt nichts", und der
  // Leerzweig darunter las ihn als "All markets stable — no active signals."
  // Ein gescheiterter Abruf sah damit aus wie die beste Nachricht, die dieses
  // Widget kennt. Der Fehlerzweig steht deshalb VOR dem Leerzweig — dieselbe
  // Form, die `PricingAutomationDashboard` (`LastRunPanel`) schon benutzt.
  const [ladeFehler, setLadeFehler] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/api/market-analytics/summary`)
      .then(({ data }) => setData(data))
      .catch(() => setLadeFehler(true))
      .finally(() => setLoading(false));
  }, []);

  const hasSignals = Boolean(data) && (
    data.signal_counts.immediate > 0 || data.signal_counts.strategic > 0
  );

  const title = loading || ladeFehler
    ? 'Market Intelligence'
    : hasSignals
      ? `Market Intelligence — ${data.signal_counts.total} alert${data.signal_counts.total !== 1 ? 's' : ''}`
      : 'Market Intelligence';

  if (ladeFehler) {
    return (
      <CollapsibleSection title={title} defaultExpanded>
        <Alert severity="error">
          Market intelligence could not be loaded — the market situation is
          unknown, not calm.
        </Alert>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection title={title} defaultExpanded={hasSignals}>
      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          Loading…
        </Typography>
      )}

      {!loading && !hasSignals && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          All markets stable — no active signals.
        </Typography>
      )}

      {!loading && data && hasSignals && (
        <Stack spacing={1.5}>
          {/* 🔴 W-33/D10: `KpiRow` + `KpiCell` + `StatTile` statt einer
              dateilokalen `KpiCell`-Nachbildung. Die lokale Fassung war ein
              Schatten des registrierten Bausteins (eigene Schriftgroessen, keine
              Klickbarkeit, kein Ladezustand) und damit die dritte Zahlenform
              auf einer Seite, die schon zwei hat. */}
          <KpiRow>
            <KpiCell>
              <StatTile
                label="Immediate alerts"
                value={data.signal_counts.immediate}
                valueColor={data.signal_counts.immediate > 0 ? 'error.main' : undefined}
              />
            </KpiCell>
            <KpiCell>
              <StatTile
                label="Strategic insights"
                value={data.signal_counts.strategic}
                valueColor={data.signal_counts.strategic > 0 ? 'warning.main' : undefined}
              />
            </KpiCell>
            <KpiCell>
              <StatTile label="Categories" value={data.monitored.categories} />
            </KpiCell>
            <KpiCell>
              <StatTile label="Competitors" value={data.monitored.competitors} />
            </KpiCell>
          </KpiRow>

          {/* Top 3 signal cards */}
          {(data.top_signals || []).map(signal => (
            <SignalCard key={signal.id} signal={signal} />
          ))}

          {/* Link to full view */}
          {data.signal_counts.total > 3 && (
            <Typography
              variant="caption"
              color="primary.main"
              sx={{ cursor: 'pointer', textAlign: 'right', display: 'block' }}
              onClick={() => navigate('/intelligence/market-lab')}
            >
              View all {data.signal_counts.total} signals in Market Lab →
            </Typography>
          )}
        </Stack>
      )}
    </CollapsibleSection>
  );
}
